import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gruposItensApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader } from '../../components/ui/Card'
import { EmptyState, LoadingState } from '../../components/ui/EmptyState'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { formatMoney } from '../../lib/utils'
import { BloqueioPremium } from './BloqueioPremium'
import { usePermiteNotas } from './usePermiteNotas'
import type { ItemSemGrupo } from '../../types'

/** Fila de itens que nenhuma regra casou com segurança: escolhe o grupo uma vez e o Walleon lembra o texto. */
export function SemGrupoPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const { permite } = usePermiteNotas()
  const { data: itens = [], isLoading } = useQuery({ queryKey: ['itens-sem-grupo'], queryFn: gruposItensApi.semGrupo, enabled: permite })
  const { data: grupos = [] } = useQuery({ queryKey: ['itens-grupos'], queryFn: gruposItensApi.list, enabled: permite })
  const { data: marcas = [] } = useQuery({ queryKey: ['itens-marcas'], queryFn: gruposItensApi.marcas, enabled: permite, staleTime: 5 * 60_000 })

  const refaz = () => {
    qc.invalidateQueries({ queryKey: ['itens-sem-grupo'] })
    qc.invalidateQueries({ queryKey: ['itens-grupos'] })
    qc.invalidateQueries({ queryKey: ['itens-grupo-detalhe'] })
    qc.invalidateQueries({ queryKey: ['nota-insights'] })
  }
  const onError = (e: Error) => toast.error(e.message)

  const reprocessar = useMutation({
    mutationFn: gruposItensApi.reprocessarHistorico,
    onSuccess: (r) => { refaz(); toast.success(`Histórico agrupado: ${r.agrupados} itens com grupo, ${r.semGrupo} sem grupo.`) },
    onError,
  })

  return (
    <>
      <PageHeader title="Itens sem grupo" />
      <div className="mb-3"><Link to="/grupos-itens" className="text-13 text-muted hover:text-white"><i className="ri-arrow-left-line" aria-hidden /> Grupos de itens</Link></div>
      {!permite ? <BloqueioPremium recurso="Os grupos de itens" /> : (
        <div className="space-y-6">
          <Card>
            <CardHeader title="Agrupar o histórico" icon="ri-refresh-line" actions={<Button variant="light" icon="ri-refresh-line" loading={reprocessar.isPending} onClick={() => reprocessar.mutate()}>Agrupar histórico agora</Button>} />
            <p className="p-4 text-13 text-muted">Aplica os grupos às notas que você já importou. Rode quando tiver terminado de resolver a fila. Suas escolhas manuais são mantidas.</p>
          </Card>

          <Card>
            <CardHeader title={`Sem grupo${itens.length ? ` (${itens.length})` : ''}`} icon="ri-question-line" />
            <p className="px-4 pt-4 text-13 text-muted">O Walleon não teve certeza do grupo destes itens. Escolha uma vez: ele lembra o texto para as próximas notas.</p>
            {isLoading ? <LoadingState /> : itens.length === 0 ? (
              <EmptyState icon="ri-checkbox-circle-line" message="Nenhum item na fila. Tudo o que entrou nas notas já tem grupo." />
            ) : (
              <div className="space-y-3 p-4">
                {itens.map((it) => <LinhaSemGrupo key={it.descricaoNormalizada} item={it} grupos={grupos} marcas={marcas} onFeito={refaz} onError={onError} />)}
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  )
}

function LinhaSemGrupo({ item, grupos, marcas, onFeito, onError }: {
  item: ItemSemGrupo; grupos: { id: string; nome: string }[]; marcas: string[]; onFeito: () => void; onError: (e: Error) => void
}) {
  const toast = useToast()
  const [grupoId, setGrupoId] = useState('')
  const [marca, setMarca] = useState(item.marcaProvavel ?? '')
  const sugeridos = item.gruposSugeridos
  const outros = grupos.filter((g) => !sugeridos.some((s) => s.id === g.id))

  const agrupar = useMutation({
    mutationFn: async () => {
      await gruposItensApi.vincularGrupo(item.descricaoNormalizada, grupoId)
      if (marca.trim() && marca.trim() !== (item.marcaProvavel ?? '')) await gruposItensApi.vincularMarca(item.descricaoNormalizada, marca.trim())
    },
    onSuccess: () => { onFeito(); toast.success('Item agrupado. O Walleon vai lembrar esta descrição.') },
    onError,
  })
  const soMarca = useMutation({
    mutationFn: () => gruposItensApi.vincularMarca(item.descricaoNormalizada, marca.trim()),
    onSuccess: () => { onFeito(); toast.success('Marca guardada.') },
    onError,
  })
  const listaId = `marcas-${item.descricaoNormalizada}`

  return (
    <div className="rounded border border-border/70 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-white">{item.descricaoOriginal}</p>
          <p className="text-2xs text-muted">{item.estabelecimento} · {formatMoney(item.ultimoValorUnitario)} · {item.quantidade} un · visto {item.ocorrencias}x</p>
        </div>
        {item.marcaProvavel && <span className="text-2xs text-muted">Marca provável: {item.marcaProvavel}</span>}
      </div>
      <div className="mt-3 grid items-end gap-3 sm:grid-cols-[1fr_12rem_auto]">
        <Select label="Grupo" value={grupoId} onChange={(e) => setGrupoId(e.target.value)}>
          <option value="">Escolha o grupo...</option>
          {sugeridos.length > 0 && <optgroup label="Sugeridos">{sugeridos.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}</optgroup>}
          <optgroup label="Todos os grupos">{outros.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}</optgroup>
        </Select>
        <div>
          <datalist id={listaId}>{marcas.map((m) => <option key={m} value={m} />)}</datalist>
          <Input label="Marca" list={listaId} value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Corrigir a marca" />
        </div>
        <div className="flex gap-2">
          <Button disabled={!grupoId} loading={agrupar.isPending} icon="ri-check-line" onClick={() => agrupar.mutate()}>Agrupar</Button>
          <Button variant="light" disabled={!marca.trim()} loading={soMarca.isPending} onClick={() => soMarca.mutate()}>Só a marca</Button>
        </div>
      </div>
    </div>
  )
}
